import { Table } from '@components/table'
import { PageRead } from '@components/table/types'
import { useFlats } from '@services/flats/queries/useFlats'
import { CircleCheck, CircleX } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { FlatList, PlaceOutput } from 'types/api'

import { cn } from '@/lib/utils'

const STATUS_TONE: Record<string, string> = {
  AVAILABLE: 'bg-success-soft text-success',
  RENTED: 'bg-info-soft text-info',
  DRAFT: 'bg-peach/25 text-warning',
  NOT_AVAILABLE: 'bg-ink/[0.07] text-ink/60'
}

export function LandlordFlats() {
  const { t } = useTranslation('flat')
  const navigate = useNavigate()
  // The backend scopes the flats list to the requesting landlord's own flats.
  const query = useFlats()

  const columns = [
    {
      title: t('table.title'),
      dataIndex: 'title',
      sorter: true,
      render: (value: string) => (
        <span className="font-medium text-ink">{value || '—'}</span>
      )
    },
    {
      title: t('table.address'),
      dataIndex: 'address',
      sorter: true,
      render: (address: string, record: FlatList) => {
        const location = record.location as PlaceOutput | null
        const village = location?.village
        const text =
          address && village
            ? `${address}, ${village}`
            : address || village || '—'
        return <span className="text-ink/80">{text}</span>
      }
    },
    {
      title: t('table.rent'),
      dataIndex: 'monthly_rent_sum',
      sorter: false,
      render: (value: number | null) =>
        value == null ? (
          <span className="text-ink/40">—</span>
        ) : (
          <span className="whitespace-nowrap font-semibold text-ink">
            {value} <span className="font-normal text-muted-foreground">€</span>
          </span>
        )
    },
    {
      title: t('table.status'),
      dataIndex: 'status',
      sorter: true,
      render: (value: string) => (
        <span
          className={cn(
            'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold',
            STATUS_TONE[value] ?? 'bg-ink/[0.07] text-ink/60'
          )}
        >
          {t(`statuses.${value}`)}
        </span>
      )
    },
    {
      title: t('table.onboarded'),
      dataIndex: 'onboarded',
      sorter: true,
      render: (value: boolean) => (
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold',
            value ? 'bg-primary/15 text-primary' : 'bg-ink/[0.07] text-ink/60'
          )}
        >
          {value ? (
            <CircleCheck className="h-3.5 w-3.5" />
          ) : (
            <CircleX className="h-3.5 w-3.5" />
          )}
          {value ? t('table.yes') : t('table.no')}
        </span>
      )
    }
  ]

  return (
    <div className="app-chrome space-y-6">
      <header className="flex items-center gap-3">
        <span
          aria-hidden
          className="h-9 w-1.5 shrink-0 rounded-full bg-gradient-to-b from-primary to-peach"
        />
        <h1 className="m-0 font-display text-2xl font-bold tracking-tight text-ink lg:text-3xl">
          {t('title')}
        </h1>
      </header>

      <Table<PageRead<FlatList>>
        query={query}
        columns={columns}
        id="landlord-flats"
        hasSearch={false}
        onRow={(record) => ({
          onClick: () => navigate(`/landlord/flats/${record.id}`)
        })}
      />
    </div>
  )
}
